/* eslint-disable react/jsx-one-expression-per-line */
import { Button } from '@pickit/pickit-components';
import React, {
  ChangeEvent,
  FocusEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  actionsTracking,
  trackingSelector,
} from '@/reducers/tracking/tracking.slice';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { getTrackingInfo } from '@/utils/tracking';
import { Banners } from '../../../constants/banners';
import { PAGE_LOAD } from '../../../constants/dataLayer';
import useHotjar from '../../../hooks/useHotjar';
import useRecaptchaV3 from '../../../hooks/useRecaptchaV3';
import pushDataLayer from '../../../utils/dataLayer';
import Banner from '../../components/Banner/Banner';
import './home.i18n';
import styles from './home.module.scss';
import { HomeProps } from './home.types';

const Home: React.FC<HomeProps> = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [hasError, setHasError] = useState(false);
  const match = useRouteMatch<{ pickitCode: string }>();
  const retailerToken = new URLSearchParams(location.search).get('token');
  const [t] = useTranslation();
  const { pickitCode, isDirty } = useSelector(trackingSelector);
  useRecaptchaV3();

  useEffect(() => {
    pushDataLayer(PAGE_LOAD);
    const auxPickitCode = match.params.pickitCode
      ? decodeURIComponent(match.params.pickitCode)
      : '';
    dispatch(actionsTracking.setPickitCode(auxPickitCode));
    dispatch(actionsTracking.setIsDirty(!!auxPickitCode));
  }, [dispatch, match?.params?.pickitCode]);

  const onBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      dispatch(actionsTracking.setIsDirty(!!event.target.value));
    },
    [dispatch],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.value) {
        setHasError(true);
      } else {
        setHasError(false);
      }
      dispatch(actionsTracking.setPickitCode(event.target.value));
      dispatch(actionsTracking.setIsDirty(!!event.target.value));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLElement>) => {
      event.preventDefault();
      if (!isDirty) {
        return;
      }
      getTrackingInfo(pickitCode, retailerToken);
    },
    [isDirty, pickitCode, retailerToken],
  );

  useHotjar('Home');

  return (
    <div className={styles.home} data-testid="home-id">
      <div className={styles.welcome}>
        <h1>{t<string>('Home:welcome')}</h1>
        <p>
          {`${t('Home:firstWelcomeParaFirstPart')} `}
          <strong>{t<string>('Home:firstWelcomeParaBold')}</strong>
          {` ${t('Home:firstWelcomeParaLastPart')} `}
          <strong>{t<string>('Home:secondWelcomePara')}</strong>
        </p>
      </div>
      <form onSubmit={handleSubmit}>
        <div className={styles.inputContainer}>
          <input
            className={isDirty && hasError ? styles.inputError : ''}
            onBlur={onBlur}
            value={pickitCode}
            placeholder={t('Home:placeHolder')}
            onChange={handleChange}
            data-testid="input-id"
          />
        </div>
        {isDirty && hasError && (
          <div className={styles.errorContainer}>
            <span className={styles.errorMessage}>
              {t<string>('Home:requiredInput')}
            </span>
          </div>
        )}
        <div className={styles.recaptchaTextContainer}>
          <p>
            {t<string>('Home:termsAndPrivacyFirstText')}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t<string>('Home:terms')}
            </a>
            {t<string>('Home:termsAndPrivacySecondText')}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t<string>('Home:privacy')}
            </a>
            .
          </p>
        </div>
        <div className={styles.buttonContainer}>
          <Button
            width="220"
            type="submit"
            label="Buscar"
            disabled={!isDirty || hasError}
          />
        </div>
      </form>
      <div className={styles.bannerContainer}>
        <Banner bannerTag={Banners.initial} retailerToken={retailerToken} />
        <a
          href={`${t<string>('Header:contactURL')}`}
          className={styles.helpLink}
        >
          {t<string>('Header:title')} {t<string>('Header:contact')}
        </a>
      </div>
    </div>
  );
};

export default Home;
